// src/hooks/useChatQueries.ts
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  InfiniteData
} from '@tanstack/react-query';
import { AxiosInstance, AxiosResponse } from 'axios';
import { useNotifications } from 'hooks/useNotifications';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { IChatApiService } from 'services/ChatApi';
import {
  SendMessageReturnType,
  Conversation,
  ChatMessage,
  ListChatsReturnType,
  GetMessagesReturnType
} from 'services/ChatApi/types';

const updateChatsOptimistically = (
  queryKey: any[],
  updater: (data: ListChatsReturnType) => ListChatsReturnType,
  queryClient: ReturnType<typeof useQueryClient>
) => {
  console.log({queryKey});
  
  queryClient.setQueryData<AxiosResponse<ListChatsReturnType>>(queryKey, (old) => {
    console.log({old});
    
    if (!old) return old;
    return {
      ...old,
      data: updater(old.data),
    };
  });
}

export const useChatQueries = (chatApi: IChatApiService<AxiosWrapper<AxiosInstance>>) => {
  const queryClient = useQueryClient();
  const PAGE_SIZE = 20;
  const notificationOps = useNotifications();
  const listChatsQuery = useQuery({
    queryKey: ['chats'],
    queryFn: () => chatApi.listChats(),
    staleTime: 1000 * 60 * 5,
  });

  const searchMessages = (keywords: string) => useQuery({
    queryKey: ['searchMessages', keywords],
    queryFn: () => chatApi.searchMessages(keywords),
    enabled: !!keywords,
  });

  const getMessagesQuery = (conversationId: string) =>
    useInfiniteQuery({
      initialPageParam: 0,
      queryKey: ['messages', conversationId],
      queryFn: ({ pageParam = 0 }) =>
        chatApi.getMessages(conversationId, pageParam, PAGE_SIZE),
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.data.hasMore ? allPages.length : undefined;
      },
      enabled: !!conversationId,
    });

  const sendNewMessage = useMutation({
    mutationKey: ['sendNewMessage'],
    mutationFn: ({ message }: { message: string; selectChat: (chatId: string) => void }) =>
      chatApi.sendNewMessage(message),

    onMutate: async ({ message, selectChat }) => {
      await queryClient.cancelQueries({ queryKey: ['chats'] });
      await queryClient.cancelQueries({ queryKey: ['messages'] });

      const previousChats = queryClient.getQueryData<AxiosResponse<ListChatsReturnType>>(['chats']);

      const tempConversationId = `temp_${Date.now()}`;
      const tempMessageId = `temp_msg_${Date.now()}`;

      const optimisticConversation: Conversation = {
        conversationId: tempConversationId,
        name: "New Chat",
        timestamp: new Date().toISOString(),
        isPinned: false,
      };
      const optimisticMessages: ChatMessage[] = [{
        messageId: tempMessageId,
        rating: null,
        message,
        response: undefined,
        timestamp: new Date().toISOString()
      }];

      queryClient.setQueryData<AxiosResponse<ListChatsReturnType>>(['chats'], (old) => {
        if (!old) return old;

        return {
          ...old,
          data: [optimisticConversation, ...old.data]
        };
      });

      queryClient.setQueryData(
        ['messages', tempConversationId],
        {
          pages: [{
            data: {
              messages: optimisticMessages,
              hasMore: true
            }
          }],
          pageParams: [0]
        },
        { updatedAt: Date.now() }
      );

      selectChat(tempConversationId);

      return {
        previousChats,
        tempConversationId,
        tempMessageId,
        message
      };
    },

    onSuccess: (data, variables, context) => {
      if (!context) return;

      queryClient.setQueryData<AxiosResponse<ListChatsReturnType>>(['chats'], (old) => {
        if (!old) return old;

        return {
          ...old,
          data: old.data.map(chat =>
            chat.conversationId === context.tempConversationId
              ? {
                ...chat,
                conversationId: data.data.conversationId,
                name: data.data.name,
              }
              : chat
          )
        };
      });

      queryClient.setQueryData(
        ['messages', data.data.conversationId],
        {
          pages: [{
            data: {
              messages: [{
                messageId: data.data.messageId,
                message: context.message,
                response: data.data.response,
                timestamp: new Date().toISOString()
              }],
              hasMore: false
            }
          }],
          pageParams: [0]
        },
        { updatedAt: Date.now() } // To Prevent refetch
      );

      variables.selectChat(data.data.conversationId);

      queryClient.removeQueries({
        queryKey: ['messages', context.tempConversationId],
        exact: true
      });
    },

    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(['chats'], context.previousChats);
      }

      queryClient.removeQueries({
        queryKey: ['messages', context?.tempConversationId],
        exact: true
      });
    }
  });
  const sendMessage = useMutation(
    {
      mutationKey: ['sendMessage'],
      mutationFn: ({ conversationId, message }: { conversationId: string; message: string }) =>
        chatApi.sendMessage(conversationId, message),
      onMutate: async ({ conversationId, message }) => {
        await queryClient.cancelQueries({ queryKey: ['messages', conversationId] });

        const previousMessages = queryClient.getQueryData<InfiniteData<GetMessagesReturnType>>(['messages', conversationId]);

        const optimisticMessage: ChatMessage = {
          messageId: `temp_msg_${Date.now()}`,
          message,
          rating: null,
          response: undefined,
          timestamp: new Date().toISOString()
        };

        queryClient.setQueryData<InfiniteData<AxiosResponse<GetMessagesReturnType>>>(
          ['messages', conversationId],
          (old: InfiniteData<AxiosResponse<GetMessagesReturnType>> | undefined) => {
            if (!old) {
              return {
                pages: [{
                  data: {
                    messages: [optimisticMessage],
                    hasMore: false
                  }
                }],
                pageParams: [0]
              } as InfiniteData<AxiosResponse<GetMessagesReturnType>>;
            }

            const newFirstPage: AxiosResponse<GetMessagesReturnType> = {
              ...old.pages[0],
              data: {
                ...old.pages[0].data,
                messages: [...old.pages[0].data.messages, optimisticMessage]
              }
            };

            return {
              ...old,
              pages: [newFirstPage, ...old.pages.slice(1)]
            };
          }
        );


        return { previousMessages, optimisticMessage };
      },
      onSuccess: ({ data }: { data: SendMessageReturnType }, variables, { optimisticMessage }) => {
        const newTimeStamp = new Date().toISOString();
        queryClient.setQueryData<InfiniteData<AxiosResponse<GetMessagesReturnType>>>(
          ['messages', variables.conversationId],
          (old: InfiniteData<AxiosResponse<GetMessagesReturnType>> | undefined) => {
            if (!old) {
              return {
                pages: [{
                  data: [{
                    conversationId: variables.conversationId,
                    messages: [optimisticMessage],
                    hasMore: false
                  }]
                }],
                pageParams: [0]
              } as unknown as InfiniteData<AxiosResponse<GetMessagesReturnType>>;
            }

            const newMessageId = old.pages[0].data.messages.findIndex((msg: ChatMessage) => msg.messageId === optimisticMessage.messageId);
            const newMessages = [...old.pages[0].data.messages];
            newMessages[newMessageId] = {
              ...newMessages[newMessageId],
              messageId: data.messageId,
              response: data.response,
              timestamp: newTimeStamp,
            };

            const newFirstPage = {
              ...old.pages[0],
              data: {
                ...old.pages[0].data,
                messages: newMessages,
              },
            };

            return {
              ...old,
              pages: [newFirstPage, ...old.pages.slice(1)]
            };
          }
        );

        queryClient.setQueryData<Conversation[]>(['chats'], (old) =>
          old?.map(conv =>
            conv.conversationId === variables.conversationId
              ? {
                ...conv,
                timestamp: newTimeStamp,
              }
              : conv
          ).sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
        );
      }
    }
  );

  const pinChat = useMutation({
    mutationFn: ({ conversationId, pin, searchQuery }: {
      conversationId: string;
      pin: boolean;
      searchQuery: string;
    }) => chatApi.pinUnpinChat(pin, conversationId),

    onMutate: async ({ conversationId, pin, searchQuery }) => {
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      const previousChats = queryClient.getQueryData<AxiosResponse<ListChatsReturnType>>(['chats']);

      updateChatsOptimistically(searchQuery ? ['searchMessages', searchQuery] : ['chats'], (data: ListChatsReturnType) => {
        return data.map(chat =>
          chat.conversationId === conversationId
            ? { ...chat, isPinned: pin }
            : chat
        );
      }, queryClient);

      return { previousChats };
    },

    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(['chats'], context.previousChats);
      }
    },

    onSettled: (_, __, { searchQuery }) => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      if (searchQuery) {
        queryClient.invalidateQueries({ queryKey: ['searchMessages', searchQuery] });
      }
    }
  });

  const renameChat = useMutation({
    mutationKey: ['renameChat'],
    mutationFn: ({ conversationId, newName, searchQuery }: { conversationId: string; newName: string, searchQuery: string }) =>
      chatApi.renameChat(conversationId, newName),
    onMutate: async ({ conversationId, newName, searchQuery }) => {
      await queryClient.cancelQueries({ queryKey: ['chats'] });
      const previousChats = queryClient.getQueryData<AxiosResponse<ListChatsReturnType>>(['chats']);

      updateChatsOptimistically(searchQuery ? ['searchMessages', searchQuery] : ['chats'], (data: ListChatsReturnType) => {
        return data.map(chat =>
          chat.conversationId === conversationId
            ? { ...chat, name: newName }
            : chat
        );
      }, queryClient);

      return { previousChats };
    },
    onError: (error, variables, context: any) => {
      if (context?.previousChats) {
        queryClient.setQueryData<AxiosResponse<ListChatsReturnType>>(['chats'], context.previousChats);
      }
    },
    onSettled: (_, __, { searchQuery }) => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      if (searchQuery) {
        queryClient.invalidateQueries({ queryKey: ['searchMessages', searchQuery] });
      }
    }
  });

  const deleteChat = useMutation({
    mutationKey: ['deleteChat'],
    mutationFn: ({ conversationId }: { conversationId: string, searchQuery: string }) => chatApi.deleteChat(conversationId),
    onMutate: async ({ conversationId, searchQuery }) => {
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      const previousChats = queryClient.getQueryData<AxiosResponse<ListChatsReturnType>>(['chats']);

      updateChatsOptimistically(searchQuery ? ['searchMessages', searchQuery] : ['chats'], (data: ListChatsReturnType) => {
        return data.filter(chat => chat.conversationId !== conversationId);
      }, queryClient);

      queryClient.removeQueries({ queryKey: ['messages', conversationId], exact: true });
      return { previousChats };
    },
    onError: (err, conversationId, context: any) => {
      if (context?.previousChats) {
        queryClient.setQueryData<AxiosResponse<ListChatsReturnType>>(['chats'], context.previousChats);
      }
    },
    onSettled: (_, __, { searchQuery }) => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      if (searchQuery) {
        queryClient.invalidateQueries({ queryKey: ['searchMessages', searchQuery] });
      }
    }
  });

  const rateMessage = useMutation(
    {
      mutationFn: ({ conversationId, messageId, rating }: { conversationId: string; messageId: string; rating: boolean }) =>
        chatApi.rateMessage(rating, messageId),
      onMutate: (variables) => {
        queryClient.setQueryData<InfiniteData<AxiosResponse<GetMessagesReturnType>>>(
          ['messages', variables.conversationId],
          (old: InfiniteData<AxiosResponse<GetMessagesReturnType>> | undefined) => {
            if (!old) return old;

            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                data: {
                  ...page.data,
                  messages: page.data.messages.map((msg: ChatMessage) =>
                    msg.messageId === variables.messageId
                      ? { ...msg, rating: variables.rating }
                      : msg
                  )
                }
              }))
            };
          }
        );
      },
      onError(error, variables, context) {
        queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
      }
    }
  );

  const getCategoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: () => chatApi.getCategories(),
    staleTime: 1000 * 60 * 60
  });

  const getPromptsByCategoryQuery = (categoryId: string) =>
    useQuery({
      queryKey: ['prompts', categoryId],
      queryFn: () => chatApi.getPromptsByCategory(categoryId),
      enabled: !!categoryId
    });

  const downloadChat = useMutation({
    mutationFn: ({ conversationId, conversationName }: { conversationId: string, conversationName: string }) => chatApi.downloadChat(conversationId),
    onSuccess: (response, { conversationName }) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${conversationName}_${new Date().toISOString().slice(0, 10)}.doc`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      notificationOps.showSuccessNotification('Export chat successfully');
    },
  });

  return {
    queries: {
      listChats: listChatsQuery,
      searchMessages,
      getMessages: getMessagesQuery,
      getCategories: getCategoriesQuery,
      getPromptsByCategory: getPromptsByCategoryQuery,
    },
    mutations: {
      sendNewMessage,
      sendMessage,
      pinChat,
      renameChat,
      deleteChat,
      rateMessage,
      downloadChat,
      searchMessages,
    }
  };
};